// Booker-facing timezone helpers for reader bookings.
//
// The old flow stamped the *reader's* timezone onto the request (InviteReaderDialog
// line 108) — so a talent in NY booking a reader in LA saw/booked the wrong hour.
// We now capture the *booker's* zone: default to their browser zone mapped to a
// coarse US label, with an explicit picker so they can override.

export interface TzOption {
  value: string;   // IANA zone stored on the booking (`timezone`)
  label: string;   // coarse label shown everywhere (`timezoneLabel`)
}

// The coarse US zones offered in the picker.
export const US_TZ_OPTIONS: TzOption[] = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
];

// Map any IANA zone to the closest coarse option (best-effort).
const ZONE_TO_OPTION: Record<string, string> = {
  'America/New_York': 'America/New_York',
  'America/Detroit': 'America/New_York',
  'America/Toronto': 'America/New_York',
  'America/Chicago': 'America/Chicago',
  'America/Denver': 'America/Denver',
  'America/Phoenix': 'America/Denver',
  'America/Los_Angeles': 'America/Los_Angeles',
  'America/Anchorage': 'America/Los_Angeles',
  'Pacific/Honolulu': 'America/Los_Angeles',
};

export function guessBrowserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
}

// The picker's default option, derived from the browser zone.
export function defaultTzOption(): TzOption {
  const zone = guessBrowserZone();
  const mapped = ZONE_TO_OPTION[zone];
  const opt = US_TZ_OPTIONS.find(o => o.value === mapped);
  return opt || US_TZ_OPTIONS[3]; // fall back to Pacific
}

export function tzOptionByValue(value: string): TzOption {
  return US_TZ_OPTIONS.find(o => o.value === value) || defaultTzOption();
}
